import Container from "../components/Container/Container";
import {Card, Form, Space, Table, Tabs, InputNumber, Button} from "antd";
import {useEffect, useState} from "react";
import useAuthHeader from "../hooks/useAuthHeader";
import {formatDateTime, numberFormat} from "../helpers/helpers";
import Search from "../components/Search/Search";
import dayjs from "dayjs";
import getPayInTop from "../api/pay-in/getTop";
import getPayOutTop from "../api/pay-out/getTop";
import TextCopy from "../components/TextCopy";
import * as XLSX from 'xlsx';
import { DownloadOutlined } from '@ant-design/icons';

function TopTransactions() {
    const accessToken = useAuthHeader();
    const [tableLoading, setTableLoading] = useState(false);
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);
    const [activeTab, setActiveTab] = useState('deposit'); // 'deposit' or 'withdraw'
    const [currentLimit, setCurrentLimit] = useState(50);
    const [tableParams, setTableParams] = useState({
        search: {
            createdAt: [dayjs().startOf('day'), dayjs().endOf('day')],
            limit: 50
        }
    });

    const items = [
        {
            name: 'createdAt',
            type: 'day',
            placeholder: 'Thời gian'
        },
        {
            name: 'limit',
            placeholder: 'Số lượng hiển thị',
            component: <InputNumber 
                min={1} 
                max={100} 
                defaultValue={50} 
                style={{ width: '100%' }} 
                onChange={(value) => setCurrentLimit(value || 50)}
            />
        }
    ];

    const depositColumns = [
        {
            title: 'Nickname',
            dataIndex: 'nickname',
            align: 'center'
        },
        {
            title: 'Số lượng GD',
            dataIndex: 'totalOrders',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Số tiền (VNĐ)',
            dataIndex: 'amount',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Phí (VNĐ)',
            dataIndex: 'fee',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Thực nhận (VNĐ)',
            dataIndex: 'actualAmount',
            align: 'right',
            render: (value) => numberFormat(value)
        }
    ];

    const withdrawColumns = [
        {
            title: 'Nickname',
            dataIndex: 'nickname',
            align: 'center'
        },
        {
            title: 'Số lượng GD',
            dataIndex: 'totalOrders',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Số tiền (VNĐ)',
            dataIndex: 'amount',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Phí (VNĐ)',
            dataIndex: 'fee',
            align: 'right',
            render: (value) => numberFormat(value)
        },
        {
            title: 'Thực trả (VNĐ)',
            dataIndex: 'actualAmount',
            align: 'right',
            render: (value) => numberFormat(value)
        }
    ];

    const handleSearch = async () => {
        setTableLoading(true);
        const values = form.getFieldsValue();
        try {
            const params = {
                fromDate: values.createdAt ? values.createdAt[0].format('YYYY-MM-DD') : null,
                toDate: values.createdAt ? values.createdAt[1].format('YYYY-MM-DD') : null,
                limit: values.limit || 50
            };

            const api = activeTab === 'deposit' ? getPayInTop : getPayOutTop;
            const response = await api(accessToken, params);
            
            if (response.success) {
                setDataSource(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setTableLoading(false);
        }
    };

    useEffect(() => {
        handleSearch();
    }, [activeTab]);

    const handleExportExcel = () => {
        const fileName = `top_${activeTab === 'deposit' ? 'nap' : 'rut'}_tien_${dayjs().format('YYYY-MM-DD')}.xlsx`;
        
        // Prepare data for export
        const exportData = dataSource.map(item => ({
            'Nickname': item.nickname,
            'Số lượng GD': item.totalOrders,
            'Số tiền (VNĐ)': item.amount,
            'Phí (VNĐ)': item.fee,
            [activeTab === 'deposit' ? 'Thực nhận (VNĐ)' : 'Thực trả (VNĐ)']: item.actualAmount
        }));

        // Create worksheet
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Data');

        // Save file
        XLSX.writeFile(wb, fileName);
    };

    const tabItems = [
        {
            key: 'deposit',
            label: `Top ${currentLimit} Nạp tiền`,
            children: (
                <>
                    <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        onClick={handleExportExcel}
                        style={{ marginBottom: 16 }}
                    >
                        Xuất Excel
                    </Button>
                    <Table
                        columns={depositColumns}
                        dataSource={dataSource}
                        loading={tableLoading}
                        pagination={false}
                        rowKey="uuid"
                    />
                </>
            )
        },
        {
            key: 'withdraw',
            label: `Top ${currentLimit} Rút tiền`,
            children: (
                <>
                    <Button 
                        type="primary" 
                        icon={<DownloadOutlined />} 
                        onClick={handleExportExcel}
                        style={{ marginBottom: 16 }}
                    >
                        Xuất Excel
                    </Button>
                    <Table
                        columns={withdrawColumns}
                        dataSource={dataSource}
                        loading={tableLoading}
                        pagination={false}
                        rowKey="uuid"
                    />
                </>
            )
        }
    ];

    return (
        <Container>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Search
                        items={items}
                        form={form}
                        onSearch={handleSearch}
                    />
                    <Tabs
                        activeKey={activeTab}
                        items={tabItems}
                        onChange={setActiveTab}
                    />
                </Space>
            </Card>
        </Container>
    );
}

export default TopTransactions; 
